import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { apiFetch } from "./client";
import type { ListResponse } from "./types";

export interface AuditEntry {
  id: string;
  tenantId: string;
  action: string;
  entityType: string;
  entityId: string;
  userId: string;
  timestamp: string;
  details?: string;
}

export type AuditSearchField = "entityId" | "entityType" | "userId" | "action";

// Every filter is applied by the server across the whole date range — the
// log is cursor-paginated, so never filter a page of it in the browser.
export interface AuditLogParams {
  limit?: number;
  cursor?: string;
  from?: string;
  to?: string;
  // Exact matches.
  entityType?: string;
  entityId?: string;
  action?: string;
  // Case-insensitive substring on searchField (entityId by default).
  search?: string;
  searchField?: AuditSearchField;
}

// useAuditActions loads the actions the backend records, for the action
// filter. The backend serves them from the one list its writers are
// checked against (backend/modules/audit/actions.go), so the filter can't
// offer an action that is never written or miss one that is.
export function useAuditActions() {
  return useQuery({
    queryKey: ["audit", "actions"],
    queryFn: () => apiFetch<{ data: string[] }>("/api/v1/audit/actions"),
    // Fixed for the life of a deploy.
    staleTime: Infinity,
  });
}

// "device_config.updated" -> "Device config updated"
export function auditActionLabel(action: string): string {
  const text = action.replace(/[._]/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function auditQuery(params: AuditLogParams = {}): string {
  const qs = new URLSearchParams();
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.entityType) qs.set("entityType", params.entityType);
  if (params.entityId) qs.set("entityId", params.entityId);
  if (params.action) qs.set("action", params.action);
  const search = params.search?.trim();
  if (search) {
    qs.set("search", search);
    qs.set("searchField", params.searchField ?? "entityId");
  }
  return qs.toString();
}

export function useAuditLog(params: AuditLogParams = {}, options: { enabled?: boolean } = {}) {
  const query = auditQuery(params);

  return useQuery({
    queryKey: ["audit", query],
    queryFn: () => apiFetch<ListResponse<AuditEntry>>(`/api/v1/audit${query ? `?${query}` : ""}`),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

// useAuditLogPages loads the log a page at a time for "Load older" lists:
// fetchNextPage appends the next page after the ones already shown.
export function useAuditLogPages(params: Omit<AuditLogParams, "cursor"> = {}) {
  const query = auditQuery(params);

  return useInfiniteQuery({
    queryKey: ["audit", "pages", query],
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams(query);
      if (pageParam) qs.set("cursor", pageParam);
      const q = qs.toString();
      return apiFetch<ListResponse<AuditEntry>>(`/api/v1/audit${q ? `?${q}` : ""}`);
    },
    initialPageParam: "",
    getNextPageParam: (last) => (last.pagination.hasMore ? last.pagination.cursor : undefined),
  });
}
